import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/formatDate';

const FILE_ICONS = { pdf: '📄', xlsx: '📊', xls: '📊', csv: '📋', txt: '📝', doc: '📝', docx: '📝' };

export default function Documents({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    authFetch('/api/companies').then(r => r.json()).then(d => setCompanies(Array.isArray(d) ? d : []));
  }, [authFetch]);

  useEffect(() => { if (selCompany) loadDocs(); }, [selCompany]);

  const loadDocs = async () => {
    setLoading(true);
    const res = await authFetch(`/api/documents/${selCompany}`);
    const d = await res.json();
    setDocs(Array.isArray(d) ? d : []);
    setLoading(false);
  };

  const upload = async (file) => {
    if (!selCompany) { showToast('Select a company first', 'error'); return; }
    if (!file) return;
    const allowed = ['.pdf','.xlsx','.xls','.csv','.txt','.doc','.docx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) { showToast('File type not supported: ' + ext, 'error'); return; }
    if (file.size > 10 * 1024 * 1024) { showToast('File too large (max 10MB)', 'error'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/documents/${selCompany}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`"${file.name}" uploaded and analyzed`);
        loadDocs();
      } else if (data.upgrade) {
        showToast(data.error, 'error');
        navigate('/billing');
      } else {
        showToast(data.error || 'Upload failed', 'error');
      }
    } catch (e) { showToast('Upload failed', 'error'); }
    setUploading(false);
  };

  const deleteDoc = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    const res = await authFetch(`/api/documents/${selCompany}/${id}`, { method: 'DELETE' });
    if (res.ok) { showToast('Document deleted'); loadDocs(); }
    else showToast('Delete failed', 'error');
  };

  const handleDrop = e => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  };

  const getFileIcon = (name) => FILE_ICONS[name?.split('.').pop()?.toLowerCase()] || '📎';
  const formatSize = (b) => b > 1024*1024 ? (b/1024/1024).toFixed(1)+'MB' : Math.round(b/1024)+'KB';

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Document Intelligence</div>
        <div className="page-sub">Upload financial reports, P&L, org charts — AI analyzes and cross-references with your audit scores</div>
      </div>

      {user?.plan === 'free' && (
        <div className="plan-banner" style={{ marginBottom: 24 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Pro Feature</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>Document upload and AI analysis requires Pro plan</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/billing')}>Upgrade to Pro →</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <label className="form-label">Select Company</label>
        <select className="form-select" style={{ maxWidth: 320 }} value={selCompany} onChange={e => setSelCompany(e.target.value)}>
          <option value="">Choose a company...</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {selCompany && (
        <>
          {/* Upload zone */}
          <div className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
            style={{ marginBottom: 24, opacity: user?.plan === 'free' ? 0.5 : 1 }}
            onClick={() => user?.plan !== 'free' && fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={user?.plan !== 'free' ? handleDrop : undefined}>
            <input ref={fileRef} type="file" style={{ display: 'none' }}
              accept=".pdf,.xlsx,.xls,.csv,.txt,.doc,.docx"
              onChange={e => upload(e.target.files[0])} />
            <div className="drop-zone-icon">{uploading ? '⏳' : '📂'}</div>
            <div style={{ fontFamily: 'var(--ff-d)', fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              {uploading ? 'Uploading & analyzing...' : 'Drop files here or click to upload'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
              Supported: PDF, Excel (.xlsx/.xls), CSV, Word, TXT · Max 10MB · 5 docs per company
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {['Balance Sheet', 'P&L Statement', 'Org Chart', 'Strategy Deck', 'Past Audit Report'].map(t => (
                <span key={t} className="tag">{t}</span>
              ))}
            </div>
          </div>

          {/* Documents list */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>
          ) : docs.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-icon">📁</div>
              <div className="empty-title">No documents yet</div>
              <div className="empty-sub">Upload your company's financial reports, org charts, or strategy documents for AI-powered analysis</div>
            </div>
          ) : (
            <div>
              {docs.map(doc => (
                <div key={doc.id} className="doc-card">
                  <div className="doc-icon" style={{ background: 'var(--blue-soft)' }}>
                    {getFileIcon(doc.original_name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="doc-name">{doc.original_name}</div>
                    <div className="doc-meta">
                      {formatSize(doc.file_size)} · {parseServerDate(doc.created_at).toLocaleDateString('en-IN')}
                    </div>
                    {doc.ai_summary && (
                      <div className="doc-summary">{doc.ai_summary.slice(0, 300)}{doc.ai_summary.length > 300 ? '...' : ''}</div>
                    )}
                    {!doc.ai_summary && (
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                        Add ANTHROPIC_API_KEY to .env to get AI analysis of uploaded documents
                      </div>
                    )}
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteDoc(doc.id, doc.original_name)}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="card" style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(0,212,255,0.03)', borderColor: 'var(--blue-soft)' }}>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
              <strong style={{ color: 'var(--gold-2)' }}>How it works:</strong> Uploaded documents are extracted and analyzed by AI. When you generate a report or chat with the AI Collaborator, these documents are referenced to provide deeper context — e.g., "Your P&L shows 40% revenue growth but your Execution score is 2.8 — here's the risk."
            </div>
          </div>
        </>
      )}
    </div>
  );
}
